import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
// import "./BusinessDashboard.css";
import store from "../../redux/store";
import { get_dashboard_data, get_ncdbcrctv, get_purchase_sales, get_purchase_sales_expenses, get_sales } from "../profile/UsersSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  get_effective_date,
  get_omc,
  get_user_id,
} from "../../redux/slice/SlideSlice";
import PageLoading from "../../components/PageLoading";
import SmallModal from "../../components/modal/SmallModal";
import Dropdown from "../../components/inputs/Dropdown";
import { getFinancialYearOptions } from "../../utils/utils";
import Table from "../../components/table/Table";
import cylinder from "../../assets/banner1.png";
import cylinderMask from "../../assets/cylinder_mask.png";
import Input from "../../components/inputs/Input";
import Button from "../../components/btn/Button";
import CustomBlockBarChart from "./CustomBlockBarChart";
import GasCylinderProgress from "./GasCylinderProgress";
import ProductDashboard from "./ProductDashboard";

const BusinessDashboard = () => {
  const [periodType, setPeriodType] = useState("3");
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [financialYear, setFinancialYear] = useState("");
  const [alerts, setAlerts] = useState(false);
  const [alertsMess, setAlertsMess] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("W1");
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
const dispatch = useDispatch();
  // Get data from Redux store
  const { dashboard_data, dashboard_loading, dashboard_error, profile_data,  purchase_sales_data,
  ncdbcrctv_data,
  sales_data,
  purchase_sales_expenses_data, } =
    useSelector((state) => state.profile);

  // Show error alert
  useEffect(() => {
    if (dashboard_error) {
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "red", textAlign: "left" }}>
          <p>{dashboard_error}</p>
        </div>
      );
    }
  }, [dashboard_error]);
  const latestFY = getFinancialYearOptions(profile_data?.CREATED_DATE)?.at(-1);
  // Fetch dashboard data on mount
  useEffect(() => {
    store.dispatch(get_dashboard_data({ fy: latestFY }));
    setFinancialYear(latestFY);
  }, [latestFY]);

  useEffect(() => {
    store.dispatch(get_effective_date());
    store.dispatch(get_user_id());
    store.dispatch(get_omc());
    store.dispatch(get_dashboard_data({ fy: latestFY }));
    setFinancialYear(latestFY);
  }, []);

  // Format number with fallback
  const formatNumber = (value) => {
    if (!value || value === "0" || value === 0) return "0";
    return parseFloat(value).toLocaleString("en-IN");
  };

  // Custom label to avoid overlap in pie charts
  const renderPieLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    name,
    value,
  }) => {
    if (!value || value <= 0) return null;

    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 24;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
        fill="#333"
      >
        {`${name} ₹${formatNumber(value)}`}
      </text>
    );
  };


  const handleFilter = () => {
    store.dispatch(
      get_dashboard_data({
        fy: financialYear,
        date: selectedDay,
        month: selectedMonth,
      })
    );
  };

  const buildDashboardPayload = ({
    filterBy,
    financialYear,
    day,
    month,
    week,
    quarter,
  }) => {
    const payload = {
      fy: financialYear || latestFY,
    };

    switch (filterBy) {
      case "Day":
        payload.date = day;
        break;

      case "Week":
        payload.week = week;
        break;

      case "Month":
        payload.month = month;
        break;

      case "Qtr":
        payload.quarter = quarter;
        break;

      case "Year":
      default:
        // only fy needed
        break;
    }

    return payload;
  };

  const handleProductDashboardFilter = (filters) => {
    const payload = buildDashboardPayload(filters);
    store.dispatch(get_dashboard_data(payload));
  };


  const handleYearChange = (event) => {
    setFinancialYear(event.target.value);
    setSelectedMonth("");
    setSelectedDay("");
  };
  // Get bank account balance by code
  const getBankBalance = (bankCode) => {
    if (!dashboard_data?.bank || !Array.isArray(dashboard_data.bank)) {
      return "0.00";
    }
    const account = dashboard_data.bank.find((v) => v.BANK_CODE === bankCode);
    return account?.ACC_CB || "0.00";
  };

  // Product Sales Data
  const productSalesData = [
    {
      category: "Domestic",
      value: parseFloat(dashboard_data?.DOM_SALES || 0),
      fill: "#4CAF50",
    },
    {
      category: "Commercial",
      value: parseFloat(dashboard_data?.COM_SALES || 0),
      fill: "#2196F3",
    },
    {
      category: "ARB",
      value: parseFloat(dashboard_data?.ARB_SALES || 0),
      fill: "#FFC107",
    },
  ];

  // Sales Distribution Data
  const salesDistributionData = [
    { name: "DOM", value: parseFloat(dashboard_data?.DOM_SALE_QTY || 0) },
    {
      name: "COM",
      value: parseFloat(dashboard_data?.COM_SALE_QTY || 0),
    },
    { name: "ARB", value: parseFloat(dashboard_data?.ARB_SALE_QTY || 0) },
  ];

  const COLORS = ["#00BCD4", "#FF9800", "#9C27B0"];

  // Additional Chart Data from API
  // Cash vs Credit Sales
  const cashCreditData = [
    {
      name: "Cash Sale",
      value: parseFloat(dashboard_data?.CASH_SALES_REVENUE || 0),
      fill: "#00BCD4",
    },
    {
      name: "Credit Sale",
      value: parseFloat(dashboard_data?.CREDIT_SALES_REVENUE || 0),
      fill: "#546E7A",
    },
  ];

  // Receipts, Payments, Expenses
  const receiptsPaymentsData = [
    {
      name: "Receipts",
      value: parseFloat(dashboard_data?.SALES_REVENUE || 0),
      fill: "#80CBC4",
    },
    {
      name: "Payments",
      value: parseFloat(dashboard_data?.PURCHASE_COST || 0),
      fill: "#00ACC1",
    },
    {
      name: "Expenses",
      value: parseFloat(dashboard_data?.PURCHASE_COST || 0) * 0.3, // Placeholder calculation
      fill: "#8BC34A",
    },
  ];

  // Sales by Type (Dom, Com, ARB, Other)
  const salesByTypeData = [
    { name: "Dom Sale", value: parseFloat(dashboard_data?.DOM_SALES || 0), fill: "#FF7043" },
    { name: "Com Sale", value: parseFloat(dashboard_data?.COM_SALES || 0), fill: "#8BC34A" },
    { name: "ARB Sale", value: parseFloat(dashboard_data?.ARB_SALES || 0), fill: "#00BCD4" },
    { name: "Other Sale", value: 0, fill: "#546E7A" },
  ];

  // Account Balances Distribution
  const accountBalancesData = [
    {
      name: "Cash On Hand",
      value: Math.abs(parseFloat(dashboard_data?.cash_balance || 0)),
      fill: "#80CBC4",
    },
    {
      name: "Receivables",
      value: Math.abs(parseFloat(dashboard_data?.receivables || 0)),
      fill: "#00ACC1",
    },
    {
      name: "Payables",
      value: Math.abs(parseFloat(dashboard_data?.payables || 0)),
      fill: "#8BC34A",
    },
  ];

  // Bank Balances Distribution
  const bankBalancesData = [
    {
      name: "Bank Balance 1",
      value: Math.abs(parseFloat(getBankBalance("SAVINGS"))),
      fill: "#AB47BC",
    },
    {
      name: "Bank Balance 2",
      value: Math.abs(parseFloat(getBankBalance("STAR ACCOUNT"))),
      fill: "#FF7043",
    },
    {
      name: "Load AC Balance",
      value: Math.abs(parseFloat(getBankBalance("TAR ACCOUNT"))),
      fill: "#FFEB3B",
    },
  ];

  // Monthly Trend Data
  const monthlyTrendData =
    dashboard_data?.MONTHLY_TREND && dashboard_data.MONTHLY_TREND.length > 0
      ? dashboard_data.MONTHLY_TREND.map((item) => ({
        week: item.week || item.month || item.MONTH || "N/A",
        sales: parseFloat(item.sales || item.SALES || 0),
        purchase: parseFloat(item.purchase || item.PURCHASE || 0),
      }))
      : [];

  // Custom label for bar chart
  const renderCustomBarLabel = (props) => {
    const { x, y, width, value } = props;
    if (!value || value === 0) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 10}
        fill="#333"
        textAnchor="middle"
        fontSize="12"
        fontWeight="600"
      >
        ₹{formatNumber(value)}
      </text>
    );
  };

  { dashboard_loading && <PageLoading /> }


  // Show no data message
  if (!dashboard_data) {
    return (
      <div className="dashboard-container">
        {dashboard_loading && <PageLoading />}
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            marginTop: "20px",
          }}
        >
          <p style={{ fontSize: "16px", color: "#666" }}>
            No dashboard data available. Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  return (
<div className="dashboard-container">


      {/* Header */}
      {/* <div className="dashboard-header">
        <div className="header-left">
          <h1>Dashboard</h1>
        </div>
      </div>

      <div
        className="inventory-management-section"
        style={{ marginBottom: 20 }}
      >
        <CustomBlockBarChart dashboard_data={dashboard_data} />
      </div>

      <div className="account-balances-section">
        <h3 className="section-title">💳 ACCOUNT BALANCES</h3>
        <div className="account-cards">
          <div className="account-card savings-card">
            <div className="account-icon">🏦</div>
            <span className="account-label">SAVINGS ACCOUNT</span>
            <span className="account-value">
              ₹{formatNumber(getBankBalance("SAVINGS"))}
            </span>
          </div>

          <div className="account-card current-card">
            <div className="account-icon">🏢</div>
            <span className="account-label">CURRENT ACCOUNT</span>
            <span className="account-value">
              ₹{formatNumber(getBankBalance("CURRENT"))}
            </span>
          </div>

          <div className="account-card loan-card">
            <div className="account-icon">💳</div>
            <span className="account-label">LOAN OUTSTANDING</span>
            <span className="account-value">
              ₹{formatNumber(getBankBalance("LOAN"))}
            </span>
          </div>

          <div className="account-card overdraft-card">
            <div className="account-icon">📊</div>
            <span className="account-label">OVERDRAFT (OD)</span>
            <span className="account-value">
              ₹{formatNumber(getBankBalance("OVER DRAFT"))}
            </span>
          </div>

          <div className="account-card cash-card">
            <div className="account-icon">💵</div>
            <span className="account-label">CASH ACCOUNT</span>
            <span className="account-value">
              ₹{formatNumber(dashboard_data?.cash_balance)}
            </span>
          </div>

          <div className="account-card load-card">
            <div className="account-icon">🚛</div>
            <span className="account-label">LOAD ACCOUNT</span>
            <span className="account-value">
              ₹{formatNumber(getBankBalance("TAR ACCOUNT"))}
            </span>
          </div>

          <div className="account-card credit-card">
            <div className="account-icon">💰</div>
            <span className="account-label">CREDIT SALES</span>
            <span className="account-value">
              ₹{formatNumber(dashboard_data?.CREDIT_SALES_REVENUE)}
            </span>
          </div>

          <div className="account-card cash-sales-card">
            <div className="account-icon">💸</div>
            <span className="account-label">CASH SALES</span>
            <span className="account-value">
              ₹{formatNumber(dashboard_data?.CASH_SALES_REVENUE)}
            </span>
          </div>

          <div className="account-card payables-card">
            <div className="account-icon">📤</div>
            <span className="account-label">PAYABLES</span>
            <span className="account-value">
              ₹{formatNumber(dashboard_data?.payables)}
            </span>
          </div>

          <div className="account-card receivables-card">
            <div className="account-icon">📥</div>
            <span className="account-label">RECEIVABLES</span>
            <span className="account-value">
              ₹{formatNumber(dashboard_data?.receivables)}
            </span>
          </div>
        </div>
      </div>

      <div
        className="dash-header-right "
        style={{
          backgroundColor: "#fff",
          padding: 10,
          borderRadius: 10,
          marginBottom: 10,
          justifyContent: "flex-end",
        }}
      >
        <div style={{ width: "180px" }}>
          <Dropdown
            label={"FILTER BY"}
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value)}
            options={
              <>
                <option value="1">DAY</option>
                <option value="2">MONTH</option>
                <option value="3">YEAR</option>
              </>
            }
          />
        </div>
        {periodType == "1" && (
          <div style={{ width: "180px" }}>
            <Input
              label={"DAY"}
              value={selectedDay}
              type="date"
              onChange={(e) => {
                setFinancialYear("");
                setSelectedMonth("");
                setSelectedDay(e.target.value);
              }}
            />
          </div>
        )}
        {(periodType == "3" || periodType == "2") && (
          <div style={{ width: "180px" }}>
            <Dropdown
              label={"FINANCIAL YEAR"}
              value={financialYear}
              onChange={handleYearChange}
              options={
                <>
                  <option value="">SELECT</option>
                  {getFinancialYearOptions(profile_data?.CREATED_DATE)?.map(
                    (v, index) => (
                      <option key={index} value={v}>
                        {v}
                      </option>
                    )
                  )}
                </>
              }
            />
          </div>
        )}
        {periodType == "2" && (
          <div style={{ width: "180px" }}>
            <Dropdown
              label={"Month"}
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
              }}
              options={
                <>
                  <option value="">SELECT</option>
                  <option value="04">APRIL</option>
                  <option value="05">MAY</option>
                  <option value="06">JUNE</option>
                  <option value="07">JULY</option>
                  <option value="08">AUGUST</option>
                  <option value="09">SEPTEMBER</option>
                  <option value="10">OCTOBER</option>
                  <option value="11">NOVEMBER</option>
                  <option value="12">DECEMBER</option>
                  <option value="01">JANUARY</option>
                  <option value="02">FEBRUARY</option>
                  <option value="03">MARCH</option>
                </>
              }
            />
          </div>
        )}
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <Button title="Apply" onClick={() => handleFilter()} />
        </div>
      </div>

      <div className="account-balances-section">
        <h3 className="chart-title">
          📊 Purchases & Sales (Daily/Weekly/Monthly selection option to be
          given default is daily)
        </h3>
        <Table
          mt={0.1}
          headData={["Product Type", "Cash", "Credit", "Total"]}
          body={
            Object.keys(dashboard_data?.purchase_sales).length > 0 ? (
              dashboard_data?.purchase_sales?.map((item, itemIndex) => (
                <tr key={itemIndex} className="product-row">
                  <td className="item-name">{item.type || "NA"}</td>
                  <td className="item-name">{item.cash}</td>
                  <td className="item-name">{item.credit}</td>
                  <td className="item-name">{item.total}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  No inventory data available
                </td>
              </tr>
            )
          }
        />
        <Table
          mt={0.1}
          headData={[]}
          body={
            Object.keys(dashboard_data?.nc_dbc).length > 0 ? (
              <>
                <tr className="product-row">
                  <td className="item-name">
                    {dashboard_data?.nc_dbc[0].type || "NA"}
                  </td>
                  <td className="item-name">{"NC"}</td>
                  <td className="item-name">{dashboard_data?.nc_dbc[0].nc}</td>
                  <td className="item-name">{"DBC"}</td>
                  <td className="item-name">{dashboard_data?.nc_dbc[0].dbc}</td>
                  <td className="item-name">
                    {dashboard_data?.nc_dbc[0].total}
                  </td>
                </tr>
                <tr className="product-row">
                  <td className="item-name">
                    {dashboard_data?.rc_tv[0].type || "NA"}
                  </td>
                  <td className="item-name">{"RC"}</td>
                  <td className="item-name">{dashboard_data?.rc_tv[0].rc}</td>
                  <td className="item-name">{"TV"}</td>
                  <td className="item-name">{dashboard_data?.rc_tv[0].tv}</td>
                  <td className="item-name">
                    {dashboard_data?.rc_tv[0].total}
                  </td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  No inventory data available
                </td>
              </tr>
            )
          }
        />
      </div>

      {/* Charts Section */}
      {/* <div className="charts-section">

        <div className="chart-card">
          <h3 className="chart-title">Product Sales Performance</h3>
          <div className="bar-chart-container">
            {productSalesData.some((item) => item.value > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={productSalesData}
                  margin={{ top: 30, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="category"
                    tick={{ fill: "#666", fontSize: 13 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                  />
                  <YAxis
                    tick={{ fill: "#999", fontSize: 11 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                    tickFormatter={(value) => `₹${formatNumber(value)}`}
                  />
                  <Tooltip
                    formatter={(value) => `₹${formatNumber(value)}`}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#8884d8"
                    radius={[8, 8, 0, 0]}
                    label={renderCustomBarLabel}
                  >
                    {productSalesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div
                style={{
                  height: "300px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#999",
                }}
              >
                No sales data available
              </div>
            )}
          </div>
        </div>

  
        <div className="chart-card">
          <h3 className="chart-title">Sales Distribution</h3>
          <div className="pie-chart-container">
            {salesDistributionData.some((item) => item.value > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={salesDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) =>
                      percent > 0
                        ? `${name} ${(percent * 100).toFixed(0)}%`
                        : ""
                    }
                    labelLine={true}
                  >
                    {salesDistributionData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatNumber(value)}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div
                style={{
                  height: "300px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#999",
                }}
              >
                No distribution data available
              </div>
            )}
          </div>
        </div>
      </div> */}

      {/* GLOBAL PERIOD FILTER – FOR PRODUCT DASHBOARD */}
{/* <div
  style={{
    background: "#fff",
    padding: "0 15px", 
    height: "50px",    // Fixed height for a slim linear bar
    borderRadius: "12px",
    marginBottom: "15px",
    display: "flex",
    alignItems: "center", // Vertically centers everything in one line
    gap: "15px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
    boxSizing: "border-box",
    width: "100%"
  }}
>
  {/* Section 1: Period Type 
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", whiteSpace: "nowrap" }}>FILTER BY:</span>
    <div style={{ width: 120 }}>
      <Dropdown
        value={periodType}
        onChange={(e) => setPeriodType(e.target.value)}
        // Using inline style to shrink the internal component height
        style={{ height: "32px", fontSize: "12px", borderRadius: "6px" }}
        options={
          <>
            <option value="Day">DAY</option>
            <option value="Week">WEEK</option>
            <option value="Month">MONTH</option>
            <option value="Qtr">QTR</option>
            <option value="Year">YEAR</option>
          </>
        }
      />
    </div>
  </div>

  {/* Visual Separator 
  <div style={{ width: "1px", height: "24px", background: "#e2e8f0" }} />

  {/* Section 2: Conditional Inputs (All Horizontal) 
  <div style={{ display: "flex", alignItems: "center", gap: "15px", flex: 1 }}>
    
    {periodType === "Day" && (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>DATE:</span>
        <div style={{ width: 150 }}>
          <Input
            type="date"
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            style={{ height: "32px", fontSize: "12px" }}
          />
        </div>
      </div>
    )}

    {(periodType === "Month" || periodType === "Qtr" || periodType === "Year" || periodType === "Week") && (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>FY:</span>
        <div style={{ width: 120 }}>
          <Dropdown
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            style={{ height: "32px", fontSize: "12px" }}
            options={
              <>
                {getFinancialYearOptions(profile_data?.CREATED_DATE)?.map((fy, i) => (
                  <option key={i} value={fy}>{fy}</option>
                ))}
              </>
            }
          />
        </div>
      </div>
    )}

    {periodType === "Month" && (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>MONTH:</span>
        <div style={{ width: 110 }}>
          <Dropdown
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{ height: "32px", fontSize: "12px" }}
            options={
              <>
                <option value="04">APR</option>
                <option value="05">MAY</option>
                <option value="06">JUN</option>
                <option value="07">JUL</option>
                <option value="08">AUG</option>
                <option value="09">SEP</option>
                <option value="10">OCT</option>
                <option value="11">NOV</option>
                <option value="12">DEC</option>
                <option value="01">JAN</option>
                <option value="02">FEB</option>
                <option value="03">MAR</option>
              </>
            }
          />
        </div>
      </div>
    )}

    {periodType === "Week" && (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>WEEK:</span>
        <div style={{ width: 140 }}>
          <Dropdown
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(e.target.value)}
            style={{ height: "32px", fontSize: "12px" }}
            options={
              <>
                <option value="1">WEEK 1 (1–7)</option>
                <option value="2">WEEK 2 (8–14)</option>
                <option value="3">WEEK 3 (15–21)</option>
                <option value="4">WEEK 4 (22–End)</option>
              </>
            }
          />
        </div>
      </div>
    )}

    {periodType === "Qtr" && (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>QTR:</span>
        <div style={{ width: 130 }}>
          <Dropdown
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(e.target.value)}
            style={{ height: "32px", fontSize: "12px" }}
            options={
              <>
                <option value="1">Q1 (Apr–Jun)</option>
                <option value="2">Q2 (Jul–Sep)</option>
                <option value="3">Q3 (Oct–Dec)</option>
                <option value="4">Q4 (Jan–Mar)</option>
              </>
            }
          />
        </div>
      </div>
    )}
  </div>

  {/* Section 3: Apply Button 
  <div style={{ marginLeft: "auto" }}>
    <Button
      title="Apply"
      onClick={() =>
        handleProductDashboardFilter({
          filterBy: periodType,
          financialYear,
          day: selectedDay,
          month: selectedMonth,
          week: selectedWeek,
          quarter: selectedQuarter,
        })
      }
      style={{
        height: "34px",
        padding: "0 20px",
        fontSize: "13px",
        fontWeight: "700",
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    />
  </div>
</div> */}

     <div>
<ProductDashboard
  dashboard_data={dashboard_data} // for KPIs & inventory
 salesData={sales_data} 
  purchaseSalesData={purchase_sales_data}
  ncDbcData={ncdbcrctv_data}
  cashCreditData={sales_data}
  purchaseSalesExpensesData={purchase_sales_expenses_data}

  onPurchaseSalesFilter={(p) => dispatch(get_purchase_sales(p))}
   onSalesFilter={(p) => dispatch(get_sales(p))} 
  onNcDbcFilter={(p) => dispatch(get_ncdbcrctv(p))}
  onCashCreditFilter={(p) => dispatch(get_sales(p))}
  onPurchaseSalesExpensesFilter={(p) =>
    dispatch(get_purchase_sales_expenses(p))
  }
/>
</div>


      {/* Error Alert Modal */}
      {alerts && (
        <SmallModal
          isOpen={alerts}
          setIsOpen={setAlerts}
          title={"Alert"}
          mess={alertsMess}
          okbtn={() => setAlerts(false)}
        />
      )}
    </div>
  );
};

export default BusinessDashboard;